import { Link, useMatch } from "react-router-dom";

import {
  SIDEBAR_GROUP_LABEL_CLASSES,
  SIDEBAR_MENU_BUTTON_CLASSES,
} from "components/sidebar/sidebarShell";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "components/ui/sidebar";
import useAuthorized from "hooks/useAuthorized";
import { cn } from "lib/utils";

// A labelled group of rail items. `conditions` gates the whole group up front
// (a feature flag, say). The items self-gate on their own permissions, so when
// the user may see none of them the menu renders empty and the group hides
// itself, keeping authorization in one place: the item.
export const SidebarSection = ({ label, icon, conditions = [], children }) => {
  if (conditions.some(condition => !condition)) {
    return null;
  }

  return (
    <SidebarGroup className="tw:has-[ul:empty]:hidden">
      {label && (
        <SidebarGroupLabel
          className={cn(SIDEBAR_GROUP_LABEL_CLASSES, icon && "tw:gap-2")}
        >
          {icon}
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu>{children}</SidebarMenu>
    </SidebarGroup>
  );
};

// A navigating rail item: gated on its own permissions and conditions, active
// while the current route matches its destination.
export const SidebarNavItem = ({
  children,
  to,
  end = false,
  icon,
  tooltip,
  permissions = [],
  conditions = [],
  className,
}) => {
  const isActive = !!useMatch({ path: to, end });
  const isAuthorized = useAuthorized(permissions);
  const { isMobile, setOpenMobile } = useSidebar();

  if (conditions.some(condition => !condition) || !isAuthorized) {
    return null;
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={isActive}
        tooltip={tooltip}
        className={cn(SIDEBAR_MENU_BUTTON_CLASSES, className)}
        // The mobile rail is a sheet over the page, so following a destination
        // has to dismiss it or the new page opens behind it.
        onClick={() => isMobile && setOpenMobile(false)}
        render={<Link to={to} />}
      >
        {icon}
        <span>{children}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};

// A trigger rail item that opens a modal: same gating and styling as a nav item
// but a plain button, so it carries no active state. Kept separate from
// SidebarNavItem so each calls its hooks unconditionally.
export const SidebarActionItem = ({
  children,
  onClick,
  icon,
  tooltip,
  permissions = [],
  conditions = [],
  className,
}) => {
  const isAuthorized = useAuthorized(permissions);

  if (conditions.some(condition => !condition) || !isAuthorized) {
    return null;
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={tooltip}
        className={cn(SIDEBAR_MENU_BUTTON_CLASSES, className)}
        onClick={onClick}
      >
        {icon}
        <span>{children}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
